import { ClipboardEvent, useRef } from 'react';
import { Paperclip } from 'lucide-react';
import { feedbackService } from '../../services/feedbackService';

// Photo / snip / video attachments for the feedback box and the admin
// conversation view — one paste-and-upload path for both (JIM-0908-09: the
// admin reply box had no paste handling at all).
export const MAX_FILES = 3;
export const ACCEPT =
  'image/*,video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm';

export function pastedFiles(e: ClipboardEvent): File[] {
  const out: File[] = [];
  for (const item of Array.from(e.clipboardData?.items ?? [])) {
    if (item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (!f) continue;
    // A snip pasted from the clipboard arrives as "image.png" — give it a
    // name a human can tell apart in the thread.
    const named =
      f.name && f.name !== 'image.png'
        ? f
        : new File([f], `snip-${Date.now()}.png`, { type: f.type });
    out.push(named);
  }
  return out;
}

export function describeFiles(files: File[]): string {
  if (!files.length) return '';
  const videos = files.filter((f) => f.type.startsWith('video/')).length;
  const photos = files.length - videos;
  const parts = [];
  if (photos) parts.push(`${photos} photo/snip${photos > 1 ? 's' : ''}`);
  if (videos) parts.push(`${videos} video${videos > 1 ? 's' : ''}`);
  return parts.join(' + ') + ' attached';
}

// Jim 10/28: three screenshot uploads from his phone answered 413 (over the
// 10 MB photo cap) and the box only said "Could not send". A phone picture
// is shrunk here before it goes — a 1600 px JPEG tells the story as well as
// a 12 MP one — and a refusal says what to do.
export const SHRINK_OVER_BYTES = 1_500_000;
export const SHRINK_MAX_EDGE = 1600;
export class AttachmentTooLarge extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttachmentTooLarge';
  }
}

export async function shrinkImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.size <= SHRINK_OVER_BYTES)
    return file;
  if (typeof createImageBitmap !== 'function') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      SHRINK_MAX_EDGE / Math.max(bitmap.width, bitmap.height)
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    );
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
      type: 'image/jpeg',
    });
  } catch {
    return file;
  }
}

export async function uploadAll(files: File[]): Promise<string[]> {
  const keys: string[] = [];
  for (const file of files.slice(0, MAX_FILES)) {
    try {
      const { s3_key } = await feedbackService.uploadPhoto(
        await shrinkImage(file)
      );
      keys.push(s3_key);
    } catch (err) {
      const status = (
        err as { response?: { status?: number }; status?: number }
      )?.response?.status;
      const flat = (err as { status?: number })?.status;
      if (status === 413 || flat === 413) {
        throw new AttachmentTooLarge(
          `${file.name || 'That file'} is too big to send (photos up to 10 MB, videos up to 60 MB). Take it off and try again.`
        );
      }
      throw err;
    }
  }
  return keys;
}

export function AttachmentRow({
  files,
  onAdd,
  onClear,
}: {
  files: File[];
  onAdd: (files: File[]) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 underline"
      >
        <Paperclip className="w-3.5 h-3.5" />
        {files.length
          ? describeFiles(files)
          : `Photo / snip / video (up to ${MAX_FILES}) — or paste a snip`}
      </button>
      {files.length > 0 && (
        <button type="button" onClick={onClear} className="underline">
          clear
        </button>
      )}
    </div>
  );
}
